/**
 * FACE WEB DE LA FRONTIÈRE DE CONFIRMATION — rend une modale, là où la face native délègue au
 * système. Voir `confirmation.tsx` pour le défaut que cette paire referme.
 *
 * TROIS RÉPONSES ONT ÉTÉ ÉCARTÉES, et les écarter est le vrai contenu de ce fichier :
 *
 *   `Alert.alert` de react-native-web — une fonction vide. C'est le défaut lui-même.
 *
 *   `window.confirm` — bloquant : il fige le fil d'exécution et la page entière, empêche toute
 *   autre commande d'arriver, et n'est ni traduisible, ni stylable, ni testable. Il rendrait le
 *   parcours pire qu'aujourd'hui sur les écrans où plusieurs confirmations s'enchaînent.
 *
 *   une modale React partagée avec le mobile — elle remplacerait la confirmation SYSTÈME d'iOS et
 *   d'Android par une surface maison. Changement visible en production, pour un défaut qui n'existe
 *   que sur le web : le rapport bénéfice/risque est le mauvais sens.
 *
 * POURQUOI DU DOM PLUTÔT QUE DU REACT. La confirmation doit pouvoir partir de n'importe où — d'un
 * `usePreventRemove`, d'un gestionnaire d'événement, d'une fonction async — donc hors de tout arbre
 * React. Monter une seconde racine React pour trois boutons imposerait `react-dom` à ce chemin et
 * un cycle de vie à synchroniser avec celui de l'application. Le DOM direct n'a ni l'un ni l'autre,
 * et les couleurs viennent quand même des tokens : la modale reste dans la charte.
 */
import { colors, radius, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export interface BoutonConfirmation {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

/** Une seule modale à la fois : une seconde demande remplace la première plutôt que de s'empiler. */
let ouverte: HTMLElement | null = null;

/**
 * L'élément qui avait le focus AVANT l'ouverture, pour le lui rendre à la fermeture.
 *
 * Sans cette restitution, le focus repart au début du document à chaque confirmation : quelqu'un
 * qui navigue au clavier doit retraverser tout l'écran pour revenir là où il en était. La modale
 * serait fermée et la personne quand même perdue.
 */
let focusDOrigine: HTMLElement | null = null;

const px = (n: number) => `${n}px`;

function couleurBouton(style: BoutonConfirmation['style']): { fond: string; texte: string } {
  if (style === 'destructive') return { fond: colors.error, texte: colors.white };
  if (style === 'cancel') return { fond: colors.grey100, texte: colors.grey700 };
  return { fond: colors.bluePrimary, texte: colors.white };
}

export function confirmer(titre: string, message: string, boutons?: BoutonConfirmation[]): void {
  fermer();

  const choix: BoutonConfirmation[] =
    boutons && boutons.length > 0 ? boutons : [{ text: t('common.ok'), style: 'cancel' }];

  const voile = document.createElement('div');
  Object.assign(voile.style, {
    position: 'fixed',
    inset: '0',
    // Pas de `backdrop-filter` : coûteux au rendu et inutile ici. Un voile sombre suffit à sortir
    // la modale de son fond sans repeindre tout l'arrière-plan.
    background: 'rgba(7, 27, 77, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: px(spacing.lg),
    zIndex: '2147483000',
  });

  const boite = document.createElement('div');
  Object.assign(boite.style, {
    background: colors.white,
    borderRadius: px(radius.lg),
    padding: px(spacing.xl),
    width: '100%',
    // La modale est un ÉLÉMENT DE LECTURE, pas un écran : elle garde sa propre largeur de colonne
    // et n'hérite pas de la borne de contenu, qui vit sur les piles de navigation.
    maxWidth: '420px',
    boxShadow: '0 18px 48px rgba(7, 27, 77, 0.28)',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  });
  boite.setAttribute('role', 'alertdialog');
  boite.setAttribute('aria-modal', 'true');

  const h = document.createElement('h2');
  h.textContent = titre;
  Object.assign(h.style, {
    margin: `0 0 ${px(spacing.sm)} 0`,
    fontSize: px(typography.h3.size),
    lineHeight: '1.3',
    color: colors.grey900,
  });
  boite.appendChild(h);

  if (message) {
    const p = document.createElement('p');
    p.textContent = message;
    Object.assign(p.style, {
      margin: `0 0 ${px(spacing.xl)} 0`,
      fontSize: px(typography.body.size),
      lineHeight: '1.5',
      color: colors.grey500,
    });
    boite.appendChild(p);
  }

  const rangee = document.createElement('div');
  Object.assign(rangee.style, { display: 'flex', flexDirection: 'column', gap: px(spacing.sm) });

  for (const bouton of choix) {
    const el = document.createElement('button');
    el.type = 'button';
    el.textContent = bouton.text;
    el.setAttribute('data-confirmation', bouton.style ?? 'default');
    const { fond, texte } = couleurBouton(bouton.style);
    Object.assign(el.style, {
      appearance: 'none',
      border: 'none',
      cursor: 'pointer',
      background: fond,
      color: texte,
      borderRadius: px(radius.md),
      padding: `${px(spacing.md)} ${px(spacing.lg)}`,
      fontSize: px(typography.body.size),
      fontWeight: '600',
      width: '100%',
    });
    // L'action part APRÈS la fermeture : un `onPress` qui navigue trouverait sinon la modale encore
    // montée par-dessus l'écran d'arrivée.
    el.addEventListener('click', () => {
      fermer();
      bouton.onPress?.();
    });
    rangee.appendChild(el);
  }

  boite.appendChild(rangee);
  voile.appendChild(boite);

  // Échap et clic hors de la boîte = ANNULER. Jamais « confirmer » : une sortie accidentelle ne
  // doit pas pouvoir déclencher l'action, surtout quand elle est destructive.
  const annuler = () => {
    const sortie = choix.find((b) => b.style === 'cancel');
    fermer();
    sortie?.onPress?.();
  };
  voile.addEventListener('click', (e) => {
    if (e.target === voile) annuler();
  });
  /**
   * ÉCHAP ANNULE, ET TAB NE SORT PAS. Sans le piège, la tabulation quitte la modale pour l'écran
   * qui est derrière le voile : on garde le focus sur des contrôles qu'on ne voit pas et qu'on ne
   * peut pas cliquer. Le clavier devient alors la seule façon de se perdre dans une boîte qui est
   * pourtant faite pour n'offrir que deux issues.
   */
  const surTouche = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      annuler();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.prototype.slice.call(rangee.querySelectorAll('button')) as HTMLButtonElement[];
    if (focusables.length === 0) return;
    const premier = focusables[0];
    const dernier = focusables[focusables.length - 1];
    const actif = document.activeElement;
    if (e.shiftKey && (actif === premier || !rangee.contains(actif))) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && (actif === dernier || !rangee.contains(actif))) {
      e.preventDefault();
      premier.focus();
    }
  };
  document.addEventListener('keydown', surTouche);
  voile.dataset.ecouteurPose = '1';
  (voile as HTMLElement & { _surTouche?: (e: KeyboardEvent) => void })._surTouche = surTouche;

  const actifAvant = document.activeElement;
  focusDOrigine = actifAvant instanceof HTMLElement ? actifAvant : null;

  document.body.appendChild(voile);
  ouverte = voile;

  // Le focus part sur le premier bouton : au clavier, la modale est utilisable sans souris, et un
  // lecteur d'écran annonce le dialogue au lieu de laisser le focus derrière le voile.
  (rangee.firstElementChild as HTMLElement | null)?.focus();
}

/**
 * FERME ET REND LE FOCUS. Exportée, parce que la modale ne s'appartient pas : voir l'appel dans
 * `RootNavigator`, qui la referme dès que la navigation bouge.
 */
export function fermerConfirmation(): void {
  fermer();
}

function fermer(): void {
  if (!ouverte) return;
  const surTouche = (ouverte as HTMLElement & { _surTouche?: (e: KeyboardEvent) => void })._surTouche;
  if (surTouche) document.removeEventListener('keydown', surTouche);
  ouverte.remove();
  ouverte = null;

  // `isConnected` : l'élément d'origine peut avoir été démonté entre-temps (c'est même le cas quand
  // la fermeture vient d'un changement d'écran). Lui rendre le focus échouerait en silence et le
  // laisserait sur `body` ; on ne tente que si l'élément est encore dans le document.
  if (focusDOrigine?.isConnected) focusDOrigine.focus();
  focusDOrigine = null;
}
